import { z } from 'zod';

// frequent-customer-memberships · Slice S1b — Membership Plan Configuration
// (spec Capability 1). Org-scoped billing plan config, org-admin only.
// interval/intervalCount mirror
// apps/backend/.../domain/value-objects/billing-interval.vo.ts; this Zod
// schema re-validates at the DTO boundary so bad requests are rejected with a
// clean message before reaching the domain entity (same pattern as
// tariff-schemas.ts / zone-schemas.ts).

export const MembershipIntervalSchema = z.enum(['week', 'month', 'quarter', 'year']);
export type MembershipInterval = z.infer<typeof MembershipIntervalSchema>;

export const CreateMembershipPlanRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  interval: MembershipIntervalSchema,
  intervalCount: z.number().int().min(1, 'intervalCount must be a positive integer'),
  priceCents: z.number().int().min(0, 'priceCents must be a non-negative integer'),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be ISO 4217 (3 uppercase letters)'),
  graceDays: z.number().int().min(0, 'graceDays must be a non-negative integer').default(0),
  maxRetries: z.number().int().min(0, 'maxRetries must be a non-negative integer').default(3),
  retryIntervalHours: z.number().int().min(1, 'retryIntervalHours must be at least 1').default(24),
});
export type CreateMembershipPlanRequest = z.infer<typeof CreateMembershipPlanRequestSchema>;

// PATCH — all fields optional; `active` toggles deactivate/reactivate
// (spec Capability 1, "Plans are deactivated, not deleted").
export const UpdateMembershipPlanRequestSchema = CreateMembershipPlanRequestSchema.partial().extend(
  {
    active: z.boolean().optional(),
  },
);
export type UpdateMembershipPlanRequest = z.infer<typeof UpdateMembershipPlanRequestSchema>;

export const MembershipPlanResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  interval: MembershipIntervalSchema,
  intervalCount: z.number().int(),
  priceCents: z.number().int(),
  currency: z.string(),
  graceDays: z.number().int(),
  maxRetries: z.number().int(),
  retryIntervalHours: z.number().int(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MembershipPlanResponse = z.infer<typeof MembershipPlanResponseSchema>;

export const ListMembershipPlansResponseSchema = z.object({
  plans: z.array(MembershipPlanResponseSchema),
});
export type ListMembershipPlansResponse = z.infer<typeof ListMembershipPlansResponseSchema>;

// frequent-customer-memberships · Slice S2b-FE — visitor-facing enrollment
// schemas (Capability 3). Promoted from a backend-internal zod schema in
// `visitor-membership.controller.ts` (S2b-BE apply-progress task 5.1 note:
// "S2b-FE should decide whether to promote the backend-internal visitor
// schemas to shared or define its own" — promoted so `apps/visitor` and the
// backend controller share a single source of truth for the request shape,
// mirroring the S1b/S2a convention for admin-facing schemas).

export const MembershipStatusSchema = z.enum([
  'pending_activation',
  'active',
  'past_due',
  'suspended',
  'canceled',
]);
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

/** POST /visitor/membership/start — no request body; response only. */
export const StartEnrollmentResponseSchema = z.object({ sent: z.literal(true) });
export type StartEnrollmentResponse = z.infer<typeof StartEnrollmentResponseSchema>;

/**
 * POST /visitor/membership/verify-otp request body. MUST stay byte-for-byte
 * identical to the regex the backend enforced locally before promotion
 * (6-digit numeric string) — this IS that schema now, imported by both
 * sides.
 */
export const VerifyOtpRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'code must be a 6-digit numeric string'),
});
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>;

export const VerifyOtpResponseSchema = z.object({ verified: z.literal(true) });
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;

/**
 * POST /visitor/membership/setup-intent response. `stripeAccountId` is
 * REQUIRED by `apps/visitor` to bind `loadStripe(pk, { stripeAccount })` to
 * the SAME connected account the SetupIntent was created on (Direct-charge
 * model, design §4) — without it the client-side Stripe.js confirmSetup call
 * would target the platform account and fail against a connected-account
 * SetupIntent. Added as a small additive field to the S2b-BE response DTO.
 */
export const CreateMembershipSetupIntentResponseSchema = z.object({
  clientSecret: z.string(),
  status: z.string(),
  stripeAccountId: z.string(),
});
export type CreateMembershipSetupIntentResponse = z.infer<
  typeof CreateMembershipSetupIntentResponseSchema
>;

export const ConfirmEnrollmentResponseSchema = z.object({ status: MembershipStatusSchema });
export type ConfirmEnrollmentResponse = z.infer<typeof ConfirmEnrollmentResponseSchema>;

// frequent-customer-memberships · Slice S3c-FE — visitor-facing SCA
// re-authentication schemas (Capability 6). `POST /reauth/start` and
// `POST /reauth/verify-otp` reuse StartEnrollmentResponseSchema /
// VerifyOtpRequestSchema / VerifyOtpResponseSchema as-is (the backend
// use-cases are purpose-agnostic, design §5). Only `/reauth/confirm` needs
// its own response shape.

/**
 * POST /visitor/membership/reauth/confirm response. `clientSecret` is
 * non-null ONLY when Stripe still reports a non-succeeded PaymentIntent —
 * the visitor FE then runs `stripe.confirmCardPayment(clientSecret)`
 * on-session and calls this endpoint again. `stripeAccountId` is present
 * whenever `clientSecret` is (needed to `loadStripe(pk, { stripeAccount })`
 * before confirming, same requirement as
 * `CreateMembershipSetupIntentResponseSchema`).
 */
export const ConfirmReauthResponseSchema = z.object({
  status: MembershipStatusSchema,
  clientSecret: z.string().nullable(),
  stripeAccountId: z.string().nullable(),
});
export type ConfirmReauthResponse = z.infer<typeof ConfirmReauthResponseSchema>;

// frequent-customer-memberships · Slice S4 — valet plate coverage lookup
// (Capability 7). `GET /valet/membership/lookup?plate=` — advisory only, the
// server-authoritative decision is enforced independently inside
// CreateTicketUseCase; this response only drives the PWA's "MEMBER — do not
// charge" banner (mirrors DuplicatePlateDialog's advisory UX).
export const MembershipLookupResponseSchema = z.object({
  isMember: z.boolean(),
  memberName: z.string().nullable(),
  membershipStatus: MembershipStatusSchema.nullable(),
  isCovered: z.boolean(),
});
export type MembershipLookupResponse = z.infer<typeof MembershipLookupResponseSchema>;
